import { useMemo, useState } from 'react';
import store from '@apply/adminStore';
import ActionSheet from 'devextreme-react/action-sheet';
import { Button, ButtonTheme } from '@apply/components';
import { ButtonBorderRadius } from '@apply/components/src/components/controls/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { IBridgeApplicationGridRecord, IBridgeApplicationPropertyGridRecord } from '@apply/api/src/api/applicationApi';
import { ApplicationStatusType } from '@apply/constants';
import { InformModal } from '../../../../../../../../common/SharedComponents';
import { getApplicationActions, onApplicationActionItem } from '../../../../../../../../applications/common/ApplicationFunctions';
import { IAppAction, IAppRowActionOptions } from '../../../../../../../../applications/common/functions/applicationActions';
import { PropertyStatusType } from '../../../../../../../../applications/common/ApplicationConstants';

export interface AppPropertyActionsButtonProps {
    application: IBridgeApplicationGridRecord;
    /** Provide when the button is on a portfolio property row. */
    property?: IBridgeApplicationPropertyGridRecord;
    parentElementId: string;
}

const toActionOptions = (
    application: IBridgeApplicationGridRecord,
    property?: IBridgeApplicationPropertyGridRecord
): IAppRowActionOptions => ({
    id: application.applicationId,
    propertyId: property?.applicationPropertyId,
    propertyStatusId: property?.propertyStatusTypeId as PropertyStatusType,
    loanTypeId: application.loanTypeId,
    appStatusId: application.applicationStatusTypeId as ApplicationStatusType,
    isPortfolio: application.isPortfolio,
    disabled: false,
    canConvertToLoan: application.canConvertToLoan,
});

export const AppPropertyActionsButton = ({ application, property, parentElementId }: AppPropertyActionsButtonProps) => {
    const [sheet, setSheet] = useState<{ visible: boolean; target: string | null }>({ visible: false, target: null });
    const [informModalOpen, setInformModalOpen] = useState(false);

    const actions = useMemo<IAppAction[]>(
        () => getApplicationActions(toActionOptions(application, property), !!property),
        [application, property]
    );

    const buttonId = `action_bridge_applications_property_${parentElementId}_${application.applicationId}_${property?.applicationPropertyId}`;

    const onButtonClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (actions.length === 0) {
            setInformModalOpen(true);
            return;
        }
        setSheet({ visible: true, target: `#${buttonId}` });
    };

    const onActionItemClick = (e) => {
        setSheet((s) => ({ ...s, visible: false }));
        onApplicationActionItem(store.dispatch, e.itemData.id, e.itemData.applicationId, e.itemData.propertyId);
    };

    return (
        <>
            <Button
                id={buttonId}
                hint='Click to see options'
                handleClick={onButtonClick}
                disabled={actions.length === 0}
                theme={ButtonTheme.GridGrey}
                borderRadius={ButtonBorderRadius.Small}
                height='20px'
                width='24px'
            >
                <FontAwesomeIcon icon={faEllipsis} />
            </Button>
            <ActionSheet
                title='Choose action'
                usePopover={true}
                showTitle={false}
                visible={sheet.visible}
                target={sheet.target}
                items={actions}
                onItemClick={onActionItemClick}
                onVisibleChange={(visible) => setSheet((s) => (s.visible === visible ? s : { ...s, visible }))}
            />
            <InformModal
                isOpen={informModalOpen}
                text='No actions available'
                confirmCallback={() => setInformModalOpen(false)}
            />
        </>
    );
};
